import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChevronDown, ArrowLeft, HelpCircle, Phone, Mail, MapPin } from "lucide-react";
import { usePageSEO } from "@/hooks/usePageSEO";

const FAQ_CATEGORIES = [
  {
    category: "Getting Started",
    items: [
      {
        q: "What is My Biz Consulting?",
        a: "My Biz Consulting is a technology-forward management consulting firm specializing in systems integration, workflow optimization, AI-driven strategy, and operational excellence. We partner with organizations, brands, and creative leaders to design scalable infrastructures that support sustainable growth."
      },
      {
        q: "Who is My Biz Consulting best suited for?",
        a: "We serve entrepreneurs, small businesses, growing organizations, enterprise clients, government agencies, and creative industry leaders. Whether you're just starting out or ready to scale, we tailor our approach to your current stage and goals."
      },
      {
        q: "How do I get started?",
        a: "The easiest way to start is by taking our free Business Clarity Assessment. It identifies bottlenecks, clarifies priorities, and generates a personalized growth roadmap. From there, you can join our free community, enroll in a program, or schedule a consultation."
      },
      {
        q: "Is there a free option?",
        a: "Yes! Our Community Membership is completely free. You get access to resources, workshops, discussion boards, and networking opportunities. The Business Clarity Assessment is also free and gives you immediate, actionable insights."
      }
    ]
  },
  {
    category: "Programs & Services",
    items: [
      {
        q: "What is the R.A.P.I.D.™ Business Recalibration Program?",
        a: "R.A.P.I.D.™ is our signature 5-session business acceleration program ($125). It's designed to help you recalibrate your business strategy, identify quick wins, and build an actionable plan — all in a structured, fast-paced format with AI-powered tools."
      },
      {
        q: "What are the Strategic Retainer options?",
        a: "We offer three tiers: Strategic Foundation ($750/3 months) with bi-weekly sessions and brand positioning; Growth & Optimization ($2,500/6 months) with weekly sessions, revenue optimization, and team building; and Scale & Leadership ($5,000/12 months) with unlimited access, executive coaching, and exit strategy planning."
      },
      {
        q: "What is the Podcast Launch Academy?",
        a: "Our 6-week Podcast Launch Academy ($500) guides you through launching a professional podcast — from concept and equipment to marketing and distribution. It includes AI content generators, marketing strategy, and a certificate of completion."
      },
      {
        q: "What are Growth Engagements?",
        a: "Growth Engagements are 10 progressive business modules that unlock throughout your 6-month program. They cover revenue acceleration, brand positioning, operations efficiency, team scaling, customer retention, digital presence, financial modeling, partnerships, leadership coaching, and exit readiness."
      },
      {
        q: "Do you offer project-based consulting?",
        a: "Yes. Our Projects Plan ($600/month) provides a dedicated project manager, custom project scope, weekly progress reports, resource allocation, and milestone tracking for specific business initiatives."
      }
    ]
  },
  {
    category: "Technology & AI Tools",
    items: [
      {
        q: "What AI tools are available on the platform?",
        a: "Our Business Solutions Center includes AI-powered tools for: Business Health Snapshots, SOP Generation, KPI Scorecards, Tech Stack Audits, Marketing Strategy, Business Plans, Brand Guidelines, Ideal Client Profiles, and more. All tools generate branded PDF reports you can share with stakeholders."
      },
      {
        q: "What are the 10 AI Business Agents?",
        a: "We have 10 specialized AI agents — each with unique personalities — that assist with onboarding, growth strategy, community engagement, networking, opportunity discovery, accountability, sales coaching, market research, content strategy, and operations optimization."
      },
      {
        q: "Who is Miles?",
        a: "Miles is our Community Engagement Director — an AI agent with authentic Philly energy who helps connect members, answers questions, and keeps the community vibrant. You can chat with Miles anytime using the chat widget on our website."
      }
    ]
  },
  {
    category: "Billing & Payments",
    items: [
      {
        q: "What payment methods do you accept?",
        a: "We accept all major credit cards (Visa, Mastercard, American Express, Discover), CashApp, Apple Pay, and Google Pay through our secure Stripe payment processing."
      },
      {
        q: "Can I upgrade or downgrade my plan?",
        a: "Yes. You can request an upgrade at any time through your dashboard. Our team will help transition you to the appropriate tier. Contact us to discuss downgrade options if your needs change."
      },
      {
        q: "Do you offer refunds?",
        a: "All sales are final. However, if you experience any issues with our services, please contact us within 7 days and we'll work to resolve the situation. We stand behind the quality of our programs and consulting services."
      }
    ]
  },
  {
    category: "Virtual Mailbox & Additional Services",
    items: [
      {
        q: "What is the Anytime Mailbox service?",
        a: "Through our Folcroft, PA operations, we offer a secure, professionally managed virtual mailbox service. It's perfect for entrepreneurs, remote teams, and businesses that need a professional business address with mail scanning, forwarding, and management capabilities."
      },
      {
        q: "Do you offer remote staffing?",
        a: "Yes. We provide remote staffing solutions starting at $2,500. This includes executive assistants, designers, tech engineers, bookkeepers, and other specialized roles deployed through our vetted talent network."
      },
      {
        q: "What is the Marketing Dashboard?",
        a: "Our Online Marketing Dashboard is a centralized platform for managing your digital marketing campaigns, tracking performance metrics, and optimizing your marketing strategy. Clients can access it at dashboard.mybizconsulting.net."
      }
    ]
  },
  {
    category: "Security & Privacy",
    items: [
      {
        q: "How secure is my data?",
        a: "We use AES-256 encryption, 24/7 monitoring, and maintain a complete audit trail. Your data security is our top priority. Visit our Security & Privacy page for full details on our practices."
      },
      {
        q: "Do you share my information with third parties?",
        a: "No. We never sell or share your personal or business information with third parties. Your data is used solely to provide our consulting services and improve your experience on our platform."
      }
    ]
  }
];

const FAQItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-neutral-200 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left group"
        data-testid={`faq-item-${q.slice(0, 20).replace(/\s/g, '-').toLowerCase()}`}
      >
        <span className="font-medium text-neutral-900 pr-4 group-hover:text-[#2BC40E] transition-colors">{q}</span>
        <ChevronDown className={`w-5 h-5 text-neutral-400 flex-shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="pb-5 pr-8 text-neutral-600 leading-relaxed text-sm animate-in fade-in duration-200">
          {a}
        </div>
      )}
    </div>
  );
};

export const FAQPage = () => {
  const navigate = useNavigate();
  usePageSEO("FAQ - Frequently Asked Questions", "Find answers about My Biz Consulting services, pricing, programs, AI tools, virtual mailbox, and security practices.");

  return (
    <div className="min-h-screen bg-white" data-testid="faq-page">
      {/* Header */}
      <header className="bg-[#333333] text-white">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/mybiz-logo.png" alt="My Biz Consulting" className="h-7 brightness-0 invert" />
            <span className="text-sm text-neutral-400">FAQ</span>
          </Link>
          <Button variant="ghost" className="text-neutral-300 hover:text-white" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-neutral-50 py-16 border-b border-neutral-200">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="w-14 h-14 bg-[#39FF14]/10 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <HelpCircle className="w-7 h-7 text-[#2BC40E]" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-neutral-900 mb-3">Frequently Asked Questions</h1>
          <p className="text-neutral-600 text-lg">Everything you need to know about My Biz Consulting and our services.</p>
        </div>
      </section>

      {/* FAQ Content */}
      <main className="max-w-3xl mx-auto px-4 py-12">
        {FAQ_CATEGORIES.map((cat) => (
          <div key={cat.category} className="mb-10">
            <h2 className="text-lg font-semibold text-neutral-900 mb-1 flex items-center gap-2">
              <div className="w-1.5 h-6 bg-[#39FF14] rounded-full" />
              {cat.category}
            </h2>
            <div className="mt-3">
              {cat.items.map((item) => (
                <FAQItem key={item.q} q={item.q} a={item.a} />
              ))}
            </div>
          </div>
        ))}
      </main>

      {/* Contact CTA */}
      <section className="bg-neutral-50 py-12 border-t border-neutral-200">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h3 className="text-xl font-semibold text-neutral-900 mb-2">Still have questions?</h3>
          <p className="text-neutral-600 mb-6">Our team is here to help. Reach out anytime.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a href="tel:215-443-1076" className="flex items-center gap-2 text-neutral-700 hover:text-[#2BC40E] transition-colors">
              <Phone className="w-4 h-4" /> 215-443-1076
            </a>
            <a href="mailto:admin@mybizconsulting.net" className="flex items-center gap-2 text-neutral-700 hover:text-[#2BC40E] transition-colors">
              <Mail className="w-4 h-4" /> admin@mybizconsulting.net
            </a>
            <span className="flex items-center gap-2 text-neutral-500">
              <MapPin className="w-4 h-4" /> Folcroft, PA 19032
            </span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
